import React, { useEffect, useRef, useState, useTransition } from 'react';
import { PrimaryButton } from './buttons/PrimaryButton';
import Dropdown from './form/Dropdown';
import Slider from '@mui/material/Slider';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { ProductCategory, productCategoryOptions } from '../constants/products';
import TimeSelector from './form/TimeSelector';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePickerComponent } from './form/DatePickerComponent';
import FullScreenOverlay from './common/OverLayComponent';
import { DateRange } from "react-day-picker";
import { DateRangePickerComponent } from './form/DateRangePickerComponent';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateFilterValues, useHandleSearch, setSearchQuery, removeAllFilters, DEFAULT_PRODUCTS_PAGE, DEFAULT_PRODUCTS_TAKE } from '../store/features/searchSlice';
import { setLoadingFinished } from '../store/features/authSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProductFilterContainerFilters } from '../api/requests/interfaces/Product';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

export enum RentalTypeEnum {
  ONE_DAY_RENTAL = "one_day_rental",
  MULTI_DAY_RENTAL = "multi_day_rental"
}

export interface FilterContainerProps {
  containerMaxHeight?: number;
  searchClick?: () => void;
}

export const FilterContainer: React.FC<FilterContainerProps> = (props) => {

  const [price, setPrice] = React.useState<number[]>([0, 100]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState<string | undefined>(undefined);
  const [endTime, setEndTime] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({ from: undefined, to: undefined });
  const [rentalDurationType, setRentalDurationType] = useState(RentalTypeEnum.MULTI_DAY_RENTAL);
  const [singleDayRentalDate, setSingleDayRentalDate] = useState<Date | undefined>(undefined);
  const [category, setCategory] = useState<ProductCategory | undefined>();
  const [location, setLocation] = useState("");
  const [title, setTitle] = useState("");

  const fixedFilterContainerRef = useRef<HTMLDivElement | null>(null);

  const [startPrice, setStartPrice] = useState(0);
  const [endPrice, setEndPrice] = useState(300);
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const UrlLocation = useLocation();

  const searchQuery = useAppSelector((s) => (s.search as any).searchQuery);
  const filterState = useAppSelector((s) => s.search.filters);
  const searchFromFilterBtn = useAppSelector((s) => s.search.searchFromFilterBtn);

  const handlePriceSliderChange = (event: Event, newValue: number | number[]) => {
    const selectedVal = newValue as number[];
    const maxPrice = 300;
    const selectStart = Math.round((maxPrice * (selectedVal[0] / 100)) * 100) / 100
    const selectEnd = Math.round((maxPrice * (selectedVal[1] / 100)) * 100) / 100
    setStartPrice(selectStart);
    setEndPrice(selectEnd);

    setPrice(selectedVal);
  };

  function valuetext(value: number) {
    return `${value}€`;
  }

  useEffect(() => {
    if (fixedFilterContainerRef.current && props.containerMaxHeight) {
      // fixedFilterContainerRef.current.style.maxHeight = props.containerMaxHeight.toString() + "px"
    }
  }, [props.containerMaxHeight])

  const handleStartPriceChange = (val: number) => {
    const currentPrice = price;
    setStartPrice(val);
    currentPrice[0] = Math.round((val / 300) * 100);
    setPrice([...currentPrice])
  }

  const handleEndPriceChange = (val: number) => {
    const currentPrice = price;
    setEndPrice(val);
    currentPrice[1] = Math.round((val / 300) * 100);
    console.log(val);
    setPrice([...currentPrice])
  }

  const { handleSearch } = useHandleSearch();

  const handleSearchClick = () => {

    props.searchClick && props.searchClick();

    handleSearch({
      page: DEFAULT_PRODUCTS_PAGE,
      take: DEFAULT_PRODUCTS_TAKE,
      title: title.length ? title : undefined,
      startDate: dateRange?.from,
      endDate: dateRange?.to,
      startTime: startTime,
      endTime: endTime,
      oneDayRentalDate: singleDayRentalDate,
      isOneDayRental: rentalDurationType === RentalTypeEnum.ONE_DAY_RENTAL,
      category: category ?? undefined,
      location: location.length ? location : undefined
    }, searchFromFilterBtn, true)
  }

  const removeAllLocalFilters = () => {
    setCategory(undefined);
    setTitle("");
    setLocation("");
    setStartPrice(0)
    setEndPrice(300)
    setDateRange(undefined);
    setStartTime(undefined);
    setEndTime(undefined);
  }

  useEffect(() => {
    setTitle(searchQuery);
  }, [searchQuery])

  useEffect(() => {
    dispatch(setSearchQuery(title))
  }, [title]);

  useEffect(() => { // Fill local filter fields from URL parameters

    const queryParams = new URLSearchParams(UrlLocation.search);
    setTitle(queryParams.get('q') || '');
    setCategory(queryParams.get('category') as ProductCategory || undefined);
    setLocation(queryParams.get('location') || '');

    let priceFrom = 0;
    let priceTo = 300;
    if (queryParams.get('price_from') !== null) {
      priceFrom = parseInt(queryParams.get('price_from')!)
    }
    if (queryParams.get('price_to') !== null) {
      priceFrom = parseInt(queryParams.get('price_to')!)
    }
    setStartPrice(priceFrom);
    setEndPrice(priceTo);
    setPrice([priceFrom, priceTo]);

    setDateRange(undefined);
    if (queryParams.get('startDate') !== null) {
      setDateRange((prevRange) => ({ from: new Date(queryParams.get('startDate')!), to: prevRange?.to }))
    }
    if (queryParams.get('endDate') !== null) {
      setDateRange((prevRange) => ({ from: prevRange?.from, to: new Date(queryParams.get('endDate')!) }))
    }

    setStartTime(queryParams.get('startTime') || '');
    setEndTime(queryParams.get('endTime') || '');

  }, [UrlLocation]);

  return (
    <div style={{ maxHeight: `${props.containerMaxHeight}px` }} className={`sticky lg:top-[80px] w-full lg:w-[400px] rounded-lg p-6 space-y-4 shadow-main z-[20]`} ref={fixedFilterContainerRef}>
      <div className="flex justify-between">
        <h2 className="text-xl font-semibold text-gray-700 text-start">Filtrid</h2>
        <div onClick={() => {removeAllLocalFilters(); removeAllFilters(); navigate("/search"); handleSearchClick();}} className='text-gray-400 cursor-pointer'>
          <DeleteOutlineOutlinedIcon />
        </div>
      </div>

      <div className={`m-0 transform transition-all duration-300`}>
        <div className="space-y-3 flex-col md:space-y-0 md:max-lg:gap-2 md:flex-row lg:space-y-3 lg:flex-col flex">
          <div className='w-full md:w-1/2 lg:w-full'>
            <div>
              <Dropdown label='Kategooria' options={productCategoryOptions} selectedFilterOptions={category ? [category] : []} placeholder='Vali kategooria' onSelect={(o) => { (o && o.length) ? setCategory(o[0] as ProductCategory) : setCategory(undefined) }} isMultiSelect={false} />
            </div>

            {/* <div>
                  <Dropdown label='Mudel' options={["Makita", "Bosch", "arawf", "awdwa"]} placeholder='Vali mudel' onSelect={(o) => { console.log('option selected ' + o) }} isMultiSelect={true} />
                </div> */}
          </div>
          <div className='w-full md:w-1/2 lg:w-full'>
            <label className="block text-sm font-medium text-gray-600 text-start pb-1">Nimi</label>
            <input
              type="text"
              placeholder="Kirjuta siia"
              value={title}
              name='title'
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-main outline-none focus:border-indigo-500 focus:ring-indigo-500 px-3 py-2"
            />
          </div>
          <div className='w-full md:w-1/2 lg:w-full'>
            <label className="block text-sm font-medium text-gray-600 text-start pb-1">Asukoht</label>
            <input
              type="text"
              placeholder="Kirjuta siia"
              value={location}
              name='location'
              onChange={(e) => setLocation(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-main outline-none focus:border-indigo-500 focus:ring-indigo-500 px-3 py-2"
            />
          </div>
          <div className='w-full md:w-1/2 lg:w-full mt-0'>
            {/* <div>
              <label className="block text-sm font-medium text-gray-600 text-start pb-1">Hind €</label>

              <div className='px-2.5'>
                <div className='w-full flex justify-between mb-[-5px]'>
                  <div>0</div>
                  <div>300</div>
                </div>
                <Slider
                  getAriaLabel={() => 'Price'}
                  value={price}
                  onChange={handlePriceSliderChange}
                  valueLabelDisplay="off"
                  getAriaValueText={valuetext}
                />
              </div>

              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Alates"
                  value={startPrice}
                  onChange={(e) => handleStartPriceChange(Number(e.target.value))}
                  className="w-full border-gray-300 rounded-md shadow-main outline-none focus:border-indigo-500 focus:ring-indigo-500 px-3 py-2"
                />
                <input
                  type="number"
                  placeholder="Kuni"
                  value={endPrice}
                  onChange={(e) => handleEndPriceChange(Number(e.target.value))}
                  className="w-full border-gray-300 rounded-md shadow-main outline-none focus:border-indigo-500 focus:ring-indigo-500 px-3 py-2"
                />
              </div>
            </div> */}

            <div>
              <label className="block text-sm font-medium text-gray-600 text-start pb-1">Rendiperiood</label>
              <RadioGroup
                row
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={rentalDurationType}
                onChange={(v) => setRentalDurationType(v.target.value as RentalTypeEnum)}
              >
                <FormControlLabel value={RentalTypeEnum.ONE_DAY_RENTAL} control={<Radio />} label="Üks päev"
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: '14px'
                    },
                  }} />
                <FormControlLabel value={RentalTypeEnum.MULTI_DAY_RENTAL} control={<Radio />} label="Mitu päeva"
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: '14px'
                    },
                  }}
                />
              </RadioGroup>
              <div className="flex space-x-2 overflow-visible">
                {
                  rentalDurationType === RentalTypeEnum.MULTI_DAY_RENTAL ?
                    <DateRangePickerComponent dateValues={dateRange} dateRangeChanged={(v) => setDateRange(v)} validChanged={(v) => { console.log('v', v) }} />
                    :
                    <div className='mb-4 flex flex-row gap-2 w-full'>
                      <div className='w-1/2'>
                        <DatePickerComponent mode='single' initialValue={singleDayRentalDate} dateChanged={(v) => setSingleDayRentalDate(v)}/>
                      </div>
                      <div className='w-1/2'>

                      </div>
                    </div>
                }
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 text-start pb-1">Kellaaeg</label>
              <div className="flex space-x-2">
                <TimeSelector value={startTime} onTimeChange={(v) => { setStartTime(v) }} />
                <TimeSelector value={endTime} onTimeChange={(v) => { setEndTime(v) }} />
                {/* <input
                  type="number"
                  placeholder="Kuni"
                  className="w-full border-gray-300 rounded-md shadow-main outline-none focus:border-indigo-500 focus:ring-indigo-500 px-3 py-2"
                /> */}
              </div>
            </div>
          </div>
        </div>
        <div className='md:max-lg:w-1/2 mt-5'>
          <PrimaryButton title={t('search')} onClick={() => handleSearchClick()} />
        </div>
      </div>
    </div>
  );
}
